import { useState, useRef, useCallback } from 'react';
import { ExternalLink, Copy, Check, Upload, Camera, Star, Music, Code, Pencil, Trash2 } from 'lucide-react';
import type { Testimonial } from '../../hooks/useBookingPageSettings';
import { palette, admin, transition } from '../../styles/tokens';
import { useBookingPageSettings } from '../../hooks/useBookingPageSettings';
import { useInvoiceBranding } from '../../hooks/useInvoiceBranding';
import { useAuthStore } from '../../stores/authStore';
import { DatePicker } from '../common/DatePicker';
import { ConfirmDialog } from '../common/ConfirmDialog';

const LABEL: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: palette.g500, marginBottom: 6, display: 'block', textTransform: 'uppercase' as const, letterSpacing: '0.04em' };
const INPUT: React.CSSProperties = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };
const CARD: React.CSSProperties = { background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, overflow: 'hidden' };
const CARD_HEAD: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: `1px solid ${palette.g100}` };
const CARD_BODY: React.CSSProperties = { padding: '16px 20px' };

const FONT_MOODS = [
  { key: 'clean', label: 'Clean', family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  { key: 'elegant', label: 'Elegant', family: "'Georgia', 'Times New Roman', serif" },
  { key: 'bold', label: 'Bold', family: "'Impact', 'Arial Black', sans-serif" },
  { key: 'vintage', label: 'Vintage', family: "'Courier New', monospace" },
];

const RESET_OPTIONS = [
  { value: 10, label: '10s' }, { value: 15, label: '15s' }, { value: 20, label: '20s' },
  { value: 30, label: '30s' }, { value: 60, label: '60s' },
];

interface Props {
  businessId: string | undefined;
  businessName?: string;
}

export function BookingPageSection({ businessId, businessName }: Props) {
  const user = useAuthStore(s => s.user);
  const { config, loading, isNew, updateConfig, uploadCoverPhoto } = useBookingPageSettings(businessId, businessName);
  const { settings: invSettings, save: saveInvSettings, uploadLogo } = useInvoiceBranding(businessId);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const coverRef = useRef<HTMLInputElement>(null);
  const logoRef = useRef<HTMLInputElement>(null);

  const save = useCallback(async (field: string, value: unknown) => {
    setSaving(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- updateConfig accepts a typed partial; dynamic-field shape requires any here
    await updateConfig({ [field]: value } as any);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, [updateConfig]);

  const saveInv = useCallback(async (field: string, value: unknown) => {
    if (!user) return;
    setSaving(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- saveInvSettings accepts a typed partial; dynamic-field shape requires any here
    await saveInvSettings({ [field]: value } as any, user.id);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, [saveInvSettings, user]);

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    await uploadCoverPhoto(file);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setSaving(true);
    await uploadLogo(file, user.id);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const copyUrl = () => {
    if (!config) return;
    void navigator.clipboard.writeText(`https://app.crewbit.ca/book/${config.slug}`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  if (loading || !config) return null;

  const bookingUrl = `https://app.crewbit.ca/book/${config.slug}`;
  const socialLinks = (config.social_links ?? {}) as Record<string, string>;

  const saveSocial = (key: string, value: string) => {
    void save('social_links', { ...socialLinks, [key]: value });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Status indicator */}
      {(saving || saved) && (
        <div style={{
          position: 'fixed', top: 68, right: 36, zIndex: 100,
          padding: '10px 20px', borderRadius: 8,
          background: saved ? palette.greenWash : palette.white,
          border: `1px solid ${saved ? palette.green : palette.g200}`,
          color: saved ? palette.green : palette.g400,
          fontSize: 13, fontWeight: 500,
          transition: `all ${transition.fast}`,
        }}>
          {saved ? '✓ Saved' : 'Saving...'}
        </div>
      )}

      {isNew && (
        <div style={{
          padding: '14px 20px', borderRadius: admin.cardRadius,
          background: palette.greenWash, border: `1px solid ${palette.green}`,
          color: palette.green, fontSize: 14, fontWeight: 500,
        }}>
          Your booking page is ready! Customize it below.
        </div>
      )}

      {/* ═══ Section 1: Booking Link ═══ */}
      <div style={CARD}>
        <div style={CARD_HEAD}>
          <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Your Booking Link</span>
        </div>
        <div style={CARD_BODY}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
            <div style={{
              flex: 1, padding: '8px 12px', borderRadius: 8, background: palette.g50,
              border: `1px solid ${palette.g200}`, fontSize: 13, color: palette.g600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace',
            }}>
              {bookingUrl}
            </div>
            <button onClick={copyUrl} style={{
              padding: '8px 14px', borderRadius: 8, border: `1px solid ${palette.g200}`,
              background: palette.white, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 12, fontWeight: 500, color: palette.g600, fontFamily: 'inherit',
            }}>
              {copied ? <Check size={14} color={palette.green} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <a href={bookingUrl} target="_blank" rel="noopener noreferrer" style={{
              padding: '8px 14px', borderRadius: 8, border: `1px solid ${palette.g200}`,
              background: palette.white, display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 12, fontWeight: 500, color: palette.accent, textDecoration: 'none', fontFamily: 'inherit',
            }}>
              <ExternalLink size={14} /> View
            </a>
          </div>

          <label style={LABEL}>Slug</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <span style={{ fontSize: 13, color: palette.g400, padding: '8px 0 8px 12px', background: palette.g50, border: `1px solid ${palette.g200}`, borderRight: 'none', borderRadius: '8px 0 0 8px', whiteSpace: 'nowrap' }}>
              app.crewbit.ca/book/
            </span>
            <input
              style={{ ...INPUT, borderRadius: '0 8px 8px 0' }}
              defaultValue={config.slug}
              onBlur={e => {
                const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
                if (val && val !== config.slug) void save('slug', val);
              }}
            />
          </div>
        </div>
      </div>

      {/* ═══ Section 2: Branding ═══ */}
      <div style={CARD}>
        <div style={CARD_HEAD}>
          <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Branding</span>
        </div>
        <div style={CARD_BODY}>
          {/* Logo */}
          <div style={{ marginBottom: 20 }}>
            <label style={LABEL}>Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {invSettings?.logo_url ? (
                <img src={invSettings.logo_url} alt="" style={{ height: 48, maxWidth: 120, borderRadius: 8, objectFit: 'contain' }} />
              ) : (
                <div style={{ width: 48, height: 48, borderRadius: 8, background: palette.g100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: palette.g400 }}>None</div>
              )}
              <button onClick={() => logoRef.current?.click()} style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 12, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Upload size={13} /> {invSettings?.logo_url ? 'Change' : 'Upload'}
              </button>
              <input ref={logoRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
            </div>
            <div style={{ fontSize: 11, color: palette.g400, marginTop: 6 }}>Appears on invoices, booking page, and client emails.</div>
          </div>

          {/* Cover photo */}
          <div style={{ marginBottom: 20 }}>
            <label style={LABEL}>Cover photo</label>
            <div style={{
              width: '100%', height: 160, borderRadius: 10, overflow: 'hidden',
              border: config.cover_photo_url ? 'none' : `2px dashed ${palette.g200}`,
              background: config.cover_photo_url ? 'none' : palette.g50,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative', cursor: 'pointer',
            }} onClick={() => coverRef.current?.click()}>
              {config.cover_photo_url ? (
                <img src={config.cover_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{ textAlign: 'center', color: palette.g400 }}>
                  <Upload size={20} style={{ marginBottom: 4 }} />
                  <div style={{ fontSize: 12 }}>Upload cover photo</div>
                </div>
              )}
            </div>
            <input ref={coverRef} type="file" accept="image/*" onChange={handleCoverUpload} style={{ display: 'none' }} />
            <div style={{ fontSize: 11, color: palette.g400, marginTop: 6 }}>1200 × 400px works best. Shown at the top of your booking page.</div>
          </div>

          {/* Accent color */}
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', marginBottom: 20 }}>
            <div>
              <label style={LABEL}>Accent color</label>
              <input type="color" value={invSettings?.accent_color || '#6CB4EE'}
                onChange={e => saveInv('accent_color', e.target.value)}
                style={{ width: 48, height: 36, border: `1px solid ${palette.g200}`, borderRadius: 8, cursor: 'pointer', padding: 2 }} />
            </div>
            <div style={{ fontSize: 11, color: palette.g400, paddingBottom: 8 }}>Used across invoices and your booking page.</div>
          </div>

          {/* Font mood */}
          <div>
            <label style={LABEL}>Font style</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {FONT_MOODS.map(fm => {
                const sel = (config.font_mood || 'clean') === fm.key;
                return (
                  <button key={fm.key} onClick={() => save('font_mood', fm.key)} style={{
                    padding: '14px 8px', borderRadius: 8, cursor: 'pointer',
                    border: sel ? `2px solid ${palette.accent}` : `1px solid ${palette.g200}`,
                    background: sel ? `${palette.accent}0A` : palette.white,
                    fontFamily: fm.family, fontSize: 15, fontWeight: 600,
                    color: sel ? palette.accent : palette.g700,
                    textAlign: 'center', transition: `all ${transition.fast}`,
                  }}
                    onMouseEnter={e => { if (!sel) e.currentTarget.style.borderColor = palette.g300; }}
                    onMouseLeave={e => { if (!sel) e.currentTarget.style.borderColor = palette.g200; }}
                  >
                    {fm.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Section 3: Page Content ═══ */}
      <div style={CARD}>
        <div style={CARD_HEAD}>
          <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Page Content</span>
        </div>
        <div style={CARD_BODY}>
          <div style={{ marginBottom: 20 }}>
            <label style={LABEL}>Headline</label>
            <input style={INPUT} defaultValue={config.headline ?? ''} maxLength={100}
              placeholder="e.g. Classic cuts. Clean fades."
              onBlur={e => save('headline', e.target.value || null)} />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={LABEL}>Description</label>
            <textarea style={{ ...INPUT, minHeight: 80, resize: 'vertical' }}
              defaultValue={config.description ?? ''} maxLength={500}
              placeholder="Tell clients about your business..."
              onBlur={e => save('description', e.target.value || null)} />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={LABEL}>Social links</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Camera size={16} color={palette.g400} />
                <input style={{ ...INPUT, flex: 1 }} defaultValue={socialLinks.instagram ?? ''}
                  placeholder="https://instagram.com/..." onBlur={e => saveSocial('instagram', e.target.value)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Music size={16} color={palette.g400} />
                <input style={{ ...INPUT, flex: 1 }} defaultValue={socialLinks.tiktok ?? ''}
                  placeholder="https://tiktok.com/..." onBlur={e => saveSocial('tiktok', e.target.value)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Star size={16} color={palette.g400} />
                <input style={{ ...INPUT, flex: 1 }} defaultValue={socialLinks.google_reviews ?? ''}
                  placeholder="https://g.page/..." onBlur={e => saveSocial('google_reviews', e.target.value)} />
              </div>
            </div>
          </div>

          <div>
            <label style={LABEL}>Cancellation policy</label>
            <textarea style={{ ...INPUT, minHeight: 60, resize: 'vertical' }}
              defaultValue={config.cancellation_policy ?? ''}
              placeholder="e.g. Please cancel at least 2 hours before your appointment."
              onBlur={e => save('cancellation_policy', e.target.value || null)} />
          </div>
        </div>
      </div>

      {/* ═══ Section 4: Booking Settings ═══ */}
      <div style={CARD}>
        <div style={CARD_HEAD}>
          <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Booking Settings</span>
        </div>
        <div style={CARD_BODY}>
          <Toggle label="Show prices" description="Display service prices on your booking page."
            checked={config.show_prices} onChange={v => save('show_prices', v)} />

          <Toggle label="Show team" description="Let clients choose their barber or stylist."
            checked={config.show_operators} onChange={v => save('show_operators', v)} />

          {config.show_operators && (
            <div style={{ marginLeft: 20, marginBottom: 16, paddingLeft: 16, borderLeft: `2px solid ${palette.g100}` }}>
              <label style={{ ...LABEL, marginBottom: 8 }}>Team selection mode</label>
              {[
                { value: 'optional', label: 'Optional — clients can pick or let you choose' },
                { value: 'required', label: 'Required — clients must pick' },
                { value: 'hidden', label: 'Hidden — you assign' },
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontSize: 13, color: palette.g700 }}>
                  <input type="radio" name="opMode" checked={config.operator_selection_mode === opt.value}
                    onChange={() => save('operator_selection_mode', opt.value)}
                    style={{ accentColor: palette.accent }} />
                  {opt.label}
                </label>
              ))}
            </div>
          )}

          <Toggle label="Allow walk-ins" description="Accept walk-in bookings from the kiosk."
            checked={config.allow_walk_ins} onChange={v => save('allow_walk_ins', v)} />

          <Toggle label="Kiosk mode" description={`Enable iPad kiosk for in-store self-service at app.crewbit.ca/kiosk/${config.slug}.`}
            checked={config.kiosk_enabled} onChange={v => save('kiosk_enabled', v)} />

          {config.kiosk_enabled && (
            <div style={{ marginLeft: 20, paddingLeft: 16, borderLeft: `2px solid ${palette.g100}`, marginBottom: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <label style={LABEL}>Kiosk greeting</label>
                <input style={INPUT} defaultValue={config.kiosk_greeting ?? ''}
                  placeholder="Welcome! Check in for your appointment."
                  onBlur={e => save('kiosk_greeting', e.target.value || null)} />
              </div>
              <div>
                <label style={LABEL}>Auto-reset after</label>
                <select value={config.kiosk_auto_reset_seconds}
                  onChange={e => save('kiosk_auto_reset_seconds', parseInt(e.target.value))}
                  style={{ ...INPUT, width: 100 }}>
                  {RESET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ Section 5: Client Reviews ═══ */}
      <TestimonialsCard testimonials={(config.testimonials ?? []) as Testimonial[]} showTestimonials={config.show_testimonials} onSave={(testimonials: Testimonial[]) => {
        const avg = testimonials.length ? testimonials.reduce((s, t) => s + t.rating, 0) / testimonials.length : 0;
        void save('testimonials', testimonials);
        void save('review_summary', { average_rating: Math.round(avg * 10) / 10, total_reviews: testimonials.length, source: 'manual' });
      }} onToggle={(v: boolean) => void save('show_testimonials', v)} />

      {/* ═══ Section 6: Embed Widget ═══ */}
      {config.slug && (
        <div style={CARD}>
          <div style={CARD_HEAD}>
            <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Code size={15} color={palette.g500} />
              Embed on your website
            </span>
          </div>
          <div style={CARD_BODY}>
            <p style={{ fontSize: 13, color: palette.g500, margin: '0 0 14px', lineHeight: 1.6 }}>
              Add your booking page to any website. Copy this code and paste it into your site.
            </p>
            <div style={{
              background: '#F7F8FA', borderRadius: 8, padding: 14, fontFamily: "'Courier New', monospace",
              fontSize: 11.5, color: palette.g700, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              border: `1px solid ${palette.g200}`, position: 'relative',
            }}>
              {`<iframe\n  src="https://app.crewbit.ca/book/${config.slug}?embed=true"\n  width="100%"\n  height="600"\n  frameborder="0"\n  style="border: none; border-radius: 12px;"\n></iframe>\n<script>\n  window.addEventListener('message', function(e) {\n    if (e.data && e.data.type === 'crewbit:resize') {\n      document.querySelector('iframe[src*="crewbit.ca/book"]').style.height = e.data.height + 'px';\n    }\n  });\n</script>`}
            </div>
            <button
              onClick={() => {
                const snippet = `<iframe\n  src="https://app.crewbit.ca/book/${config.slug}?embed=true"\n  width="100%"\n  height="600"\n  frameborder="0"\n  style="border: none; border-radius: 12px;"\n></iframe>\n<script>\n  window.addEventListener('message', function(e) {\n    if (e.data && e.data.type === 'crewbit:resize') {\n      document.querySelector('iframe[src*="crewbit.ca/book"]').style.height = e.data.height + 'px';\n    }\n  });\n</script>`;
                void navigator.clipboard.writeText(snippet);
                setEmbedCopied(true); setTimeout(() => setEmbedCopied(false), 2000);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, marginTop: 12,
                padding: '8px 16px', borderRadius: 8, border: `1px solid ${palette.g200}`,
                background: palette.white, fontSize: 13, fontWeight: 500,
                color: embedCopied ? palette.green : palette.g600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {embedCopied ? <Check size={14} /> : <Copy size={14} />}
              {embedCopied ? 'Copied!' : 'Copy embed code'}
            </button>
            <p style={{ fontSize: 12, color: palette.g400, margin: '12px 0 0', lineHeight: 1.5 }}>
              Works with WordPress, Squarespace, Wix, and any website that supports HTML embeds.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Testimonials manager card ──

const MAX_TESTIMONIALS = 20;

function StarRating({ value, onChange, size = 18 }: { value: number; onChange?: (v: number) => void; size?: number }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24"
          fill={i <= value ? palette.accent : palette.g200}
          style={{ cursor: onChange ? 'pointer' : 'default' }}
          onClick={() => onChange?.(i)}
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

function TestimonialsCard({ testimonials, showTestimonials, onSave, onToggle }: {
  testimonials: Testimonial[];
  showTestimonials: boolean;
  onSave: (t: Testimonial[]) => void;
  onToggle: (v: boolean) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [service, setService] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [confirmDelete, setConfirmDelete] = useState<Testimonial | null>(null);

  const avg = testimonials.length ? testimonials.reduce((s, t) => s + t.rating, 0) / testimonials.length : 0;

  const resetForm = () => { setName(''); setRating(5); setText(''); setService(''); setDate(new Date().toISOString().split('T')[0]); setAdding(false); setEditing(null); };

  const handleAdd = () => {
    if (!name.trim() || !text.trim() || rating < 1) return;
    const t: Testimonial = { id: crypto.randomUUID(), name: name.trim(), text: text.trim(), rating, date, service: service.trim() };
    onSave([...testimonials, t]);
    resetForm();
  };

  const handleUpdate = () => {
    if (!editing || !name.trim() || !text.trim()) return;
    onSave(testimonials.map(t => t.id === editing ? { ...t, name: name.trim(), text: text.trim(), rating, date, service: service.trim() } : t));
    resetForm();
  };

  const startEdit = (t: Testimonial) => {
    setEditing(t.id); setName(t.name); setRating(t.rating); setText(t.text); setService(t.service); setDate(t.date); setAdding(true);
  };

  return (
    <div style={CARD}>
      <div style={CARD_HEAD}>
        <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Client Reviews</span>
        <span style={{ fontSize: 12, color: palette.g400 }}>{testimonials.length} of {MAX_TESTIMONIALS}</span>
      </div>
      <div style={CARD_BODY}>
        <Toggle label="Show reviews on booking page" description="Display testimonials on your public booking page."
          checked={showTestimonials} onChange={onToggle} />

        {/* Summary */}
        {testimonials.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <StarRating value={Math.round(avg)} size={16} />
            <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>{(Math.round(avg * 10) / 10).toFixed(1)}</span>
            <span style={{ fontSize: 12, color: palette.g400 }}>({testimonials.length} {testimonials.length === 1 ? 'review' : 'reviews'})</span>
          </div>
        )}

        {/* List */}
        {testimonials.map(t => (
          <div key={t.id} style={{ padding: '12px 0', borderTop: `1px solid ${palette.g100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <StarRating value={t.rating} size={12} />
              <div style={{ fontSize: 13, color: palette.g800, marginTop: 4, lineHeight: 1.5 }}>{t.text}</div>
              <div style={{ fontSize: 11, color: palette.g400, marginTop: 4 }}>
                — {t.name}{t.service ? ` · ${t.service}` : ''}{t.date ? ` · ${t.date}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <button onClick={() => startEdit(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: palette.g400 }}>
                <Pencil size={13} />
              </button>
              <button onClick={() => setConfirmDelete(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: palette.red }}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}

        {/* Add / Edit form */}
        {adding ? (
          <div style={{ marginTop: 12, padding: 16, background: palette.g50, borderRadius: 8, border: `1px solid ${palette.g150}` }}>
            <div style={{ marginBottom: 10 }}>
              <label style={LABEL}>Rating</label>
              <StarRating value={rating} onChange={setRating} size={22} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={LABEL}>Client name *</label>
              <input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="Sarah M." />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={LABEL}>Review text *</label>
              <textarea style={{ ...INPUT, height: 72, resize: 'vertical' }} value={text} onChange={e => setText(e.target.value.slice(0, 300))} placeholder="Always on time, always perfect." />
              <div style={{ fontSize: 11, color: palette.g400, textAlign: 'right', marginTop: 2 }}>{text.length} / 300</div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={LABEL}>Service (optional)</label>
              <input style={INPUT} value={service} onChange={e => setService(e.target.value)} placeholder="Gel Nails" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={LABEL}>Date</label>
              <div style={{ width: 200 }}>
                <DatePicker value={date || null} onChange={(v) => setDate(v ?? '')} maxDate={new Date().toISOString().split('T')[0]} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={editing ? handleUpdate : handleAdd} disabled={!name.trim() || !text.trim()} style={{
                padding: '7px 16px', borderRadius: 6, border: 'none', fontSize: 13, fontWeight: 600,
                fontFamily: 'inherit', cursor: !name.trim() || !text.trim() ? 'not-allowed' : 'pointer',
                background: !name.trim() || !text.trim() ? palette.g200 : palette.accent, color: palette.white,
              }}>{editing ? 'Update' : 'Add Review'}</button>
              <button onClick={resetForm} style={{
                padding: '7px 16px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13,
                fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', background: palette.white, color: palette.g600,
              }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => { if (testimonials.length < MAX_TESTIMONIALS) setAdding(true); }} disabled={testimonials.length >= MAX_TESTIMONIALS} style={{
            marginTop: 12, padding: '8px 16px', borderRadius: 6,
            border: `1px dashed ${testimonials.length >= MAX_TESTIMONIALS ? palette.g200 : palette.g300}`,
            background: 'none', fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
            color: testimonials.length >= MAX_TESTIMONIALS ? palette.g300 : palette.g600,
            cursor: testimonials.length >= MAX_TESTIMONIALS ? 'not-allowed' : 'pointer', width: '100%',
          }}>+ Add a review</button>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title={`Remove ${confirmDelete?.name ?? 'this'}'s review?`}
        message="The review will no longer appear on your booking page. This cannot be undone — you would need to re-enter the review to restore it."
        confirmLabel="Remove review"
        confirmVariant="danger"
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) onSave(testimonials.filter(t => t.id !== confirmDelete.id));
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

// ── Toggle switch ──

function Toggle({ label, description, checked, onChange }: {
  label: string; description: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: palette.g800 }}>{label}</div>
        <div style={{ fontSize: 12, color: palette.g400, marginTop: 2 }}>{description}</div>
      </div>
      <button onClick={() => onChange(!checked)} style={{
        width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
        background: checked ? palette.accent : palette.g200,
        position: 'relative', transition: `background ${transition.fast}`,
        flexShrink: 0,
      }}>
        <div style={{
          width: 20, height: 20, borderRadius: 10, background: '#fff',
          position: 'absolute', top: 2,
          left: checked ? 22 : 2,
          transition: `left ${transition.fast}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }} />
      </button>
    </div>
  );
}
